export default {
  domain: 'C',
  title: 'Gewissenhaftigkeit',
  shortDescription: 'Gewissenhaftigkeit beschäftigt sich mit der Art, wie wir unsere Impulse kontrollieren, regulieren und steuern.',
  description: `Impulse sind nicht grundsätzlich schlecht; gelegentlich ist es 
notwendig wegen Zeitdruck mit einer spontanen Entscheidung zu reagieren
und mit dem ersten Impuls ist es eine effektive Reaktion. Außerdem
kann es in nicht ernsten Lagen spaßig sein spontan und impulsiv zu reagieren. 
Impulsive Individuen können von anderen als facettenreich, kasperhaft und 
gesellig angesehen werden.
<br /><br />
Dennoch kann impulsives Handeln zu Problemen führen. Manche Impulse sind
asozial. Unkontrolliertes asoziales Handeln kann nicht nur anderen schaden,
es kann ebenfalls zu Rachegelüsten gegenüber dem Täter der impulsiven 
Handlungen führen. Ein weiteres Problem sind die ungewollten langfristigen
Konsequenzen, auch wenn vorerst sofortige Belohnungen entstehen. Zum Beispiel
der Verlust eines Jobs nach übermäßigem Sozialisieren, der Zusammenbruch einer
wichtigen Beziehung nachdem eine Beleidigung gefallen ist oder die Zerstörung
der eigenen Gesundheit durch rekreative Drogen.
<br /><br />
Impulsives Verhalten, auch wenn dieses nicht wirklich schädlich ist, reduziert
die Effektivität der Person in mehreren signifikanten Weisen. Das impulsive 
Handeln verhindert alternative Herangehensweisen an ein Problem, welche
weiser als die impulsive Entscheidung gewesen wären. Impulsivität lenkt 
den Menschen ab, wenn ein Projekt einen organisierten Plan verfolgt. 
Errungenschaften einer impulsiven Person sind daher klein, weit gestreut und
inkonsistent.
<br /><br />
Ein Kennzeichen von Intelligenz, etwas was potentiell den Menschen von früheren
Lebensformen unterscheidet, ist die Fähigkeit über zukünftige Konsequenzen
nachzudenken, bevor nach einem Impuls gehandelt wird. Intelligente Aktivitäten 
erfordern das Reflektieren über Langzeitziele, das Planen und Organisieren der
Schritte zu diesem Ziel, sowie das Durchhaltevermögen für das Ziel gegenüber
kurzzeitigen Impulsen. Die Idee, dass Intelligenz das Kontrollieren von Impulsen
beinhaltet, deckt sich mit dem Begriff Vernunft, welches ein anderer Titel für
die Gewissenhaftigkeitsdomäne ist. Vernunft bedeutet hier sowohl Weise als auch
Behutsam zu sein.
<br /><br/>
Personen mit hoher Punktzahl in der Gewissenhaftigkeitsskala werden in der Tat
von anderen als intelligent wahrgenommen. Die Vorteile von hoher 
Gewissenhaftigkeit sind offensichtlich. Gewissenhafte Individuen vermeiden 
Schwierigkeiten und haben ein hohes Maß an Erfolg durch zielgerichtetes Planen
und Beständigkeit. Sie sind außerdem von anderen als intelligent und 
zuverlässig angesehen. Auf der anderen Seite können sie zwanghafte 
Perfektionisten und Workaholics sein. Des weiteren könnten extrem gewissenhafte
Leute als spießig und langweilig angesehen werden.
<br /><br />
Ungewissenhafte Menschen können für ihre Unzuverlässigkeit, Mangel an Ehrgeiz,
und das Scheitern im Rahmen zu bleiben kritisiert werden, aber sie durchleben
viele kurzzeitige Vergnügungen und werden niemals als spießig angesehen.`,
  results: [
    {
      score: 'low',
      text: `Deine Punktzahl in Gewissenhaftigkeit ist niedrig, was darauf 
hinweist, dass du für den Moment lebst und tust, was sich für dich im Moment gut
anfühlt. Deine Arbeitsweise neigt dazu unbekümmert und unorganisiert zu sein.`
    },
    {
      score: 'neutral',
      text: `Deine Punktzahl in Gewissenhaftigkeit ist mittelmäßig. Das 
bedeutet, dass du ziemlich zuverlässig und organisiert bist und dich selbst
beherrscht.`
    },
    {
      score: 'high',
      text: `Deine Punktzahl in Gewissenhaftigkeit ist hoch. Das bedeutet, dass
du dir klare Ziele setzt und diese mit Entschlossenheit verfolgst. Andere Leute
sehen dich als zuverlässig und fleißig.`
    }
  ],
  facets: [
    {
      facet: 1,
      title: 'Kompetenz',
      text: `Kompetenz beschreibt die Selbsticherheit in die eigene Fähigkeit
Dinge zu erreichen. Menschen mit hoher Punktzahl glauben, sie haben die nötige
Intelligenz (gesunden Menschenverstand), Trieb und Selbstkontrolle, um einen
Erfolg zu erzielen. Menschen mit niedriger Punktzahl fühlen sich ineffektiv,
und können den Gedanken haben, ihr Leben nicht im Griff zu haben.`
    },
    {
      facet: 2,
      title: 'Ordnungsliebe',
      text: `Personen mit hoher Punktzahl in Ordnungsliebe sind gut 
organisiert. Sie lieben es, ihr Leben in Routinen und Termine einzuteilen. Sie
schreiben Listen und machen Pläne. Menschen mit niedriger Punktzahl neigen dazu
unorganisiert und zerstreut zu sein.`
    },
    {
      facet: 3,
      title: 'Pflichtbewusstsein',
      text: `Diese Skala reflektiert die Stärke der Pflichtauffassung einer 
Person. Jene, die eine hohe Punktzahl in dieser Skala erzielen, haben einen starken
Drang ihren moralischen Pflichten nachzugehen. Menschen mit niedriger Punktzahl
finden Verträge, Regeln, und Vorschriften zu begrenzend. Sie neigen dazu als 
unzuverlässig oder sogar verantwortungslos gesehen zu werden.`
    },
    {
      facet: 4,
      title: 'Leistungsstreben',
      text: `Individuen, die eine hohe Punktzahl in dieser Skala erreichen, 
streben sehr nach Spitzenleistungen. Ihr als erfolgreich angesehenes Streben
hält sie auf der Spur für ihre hoch gesteckten Ziele. Sie haben oft einen
konkreten Lebensweg, aber Menschen mit extrem hohen Punktzahlen sind möglicherweise zu 
zielgerichtet und besessen mit ihrer Arbeit. Menschen mit niedriger Punktzahl
genügt es ihre Arbeit mit minimalem Aufwand zu erledigen und können von
anderen als faul gesehen werden.`
    },
    {
      facet: 5,
      title: 'Selbstdisziplin',
      text: `Selbstdisziplin, welches viele Leute auch Willenskraft nennen,
beschreibt die Fähigkeit an einem Problem oder einer unangenehmen Tätigkeit
konsequent zu arbeiten, bis diese erledigt ist. Leute mit hoher Selbstdisziplin
sind in der Lage den Widerwillen zu bezwingen um Aufgaben zu beginnen und 
bis zum Ende zu führen, ohne abgelenkt zu werden. Jene mit wenig Selbstdisziplin
schieben ihre Arbeiten auf und zeigen wenig Durchhaltevermögen. Dabei scheitern
sie daran ihre Aufgaben zu erledigen, auch wenn sie das durchaus möchten.`
    },
    {
      facet: 6,
      title: 'Besonnenheit',
      text: `Besonnenheit beschreibt die Einstellung die möglichen Ergebnisse
zu durchdenken, bevor gehandelt wird. Menschen mit hoher Punktzahl in 
Besonnenheit nehmen sich die Zeit und denken über ihre Entscheidungen nach.
Menschen mit niedriger Punktzahl handeln oft nach dem ersten Gedanken ohne 
über Alternativen und mögliche Konsequenzen dieser Alternativen nachzudenken.`
    }
  ]
};
